import { formatInTimeZone } from 'date-fns-tz';

export interface AttendanceConflictWindow {
  start: string | Date;
  end: string | Date;
}

export interface AttendanceConflictGambit {
  id: string;
  title: string;
  start: string | Date;
  end: string | Date;
  isAttending?: boolean;
  timezone?: string;
  locationSummary?: { name?: string | null } | null;
}

/* Returns the first gambit the player attends that overlaps the window, skipping
   the ids of the card's own gambit and the gambit a proposal merged into. */
export function findAttendanceConflict<T extends AttendanceConflictGambit>(
  window: AttendanceConflictWindow,
  attendedGambits: T[],
  excludeGambitIds: Array<string | null | undefined> = [],
): T | null {
  const windowStart = new Date(window.start).getTime();
  const windowEnd = new Date(window.end).getTime();

  if (!Number.isFinite(windowStart) || !Number.isFinite(windowEnd) || windowStart >= windowEnd) {
    return null;
  }

  const excluded = new Set(excludeGambitIds.filter((id): id is string => !!id));

  return (
    attendedGambits.find((gambit) => {
      if (gambit.isAttending === false || excluded.has(gambit.id)) {
        return false;
      }

      const gambitStart = new Date(gambit.start).getTime();
      const gambitEnd = new Date(gambit.end).getTime();

      return windowStart < gambitEnd && windowEnd > gambitStart;
    }) || null
  );
}

/* Spot and local start time, e.g. ["Favoritenstraße 73", "16:00"]; falls back to the
   title when the spot is unknown so the warning still names something. */
export function getAttendanceConflictLabel(gambit: AttendanceConflictGambit): [string, string] {
  const timeZone = gambit.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const time = formatInTimeZone(gambit.start, timeZone, 'HH:mm');

  return [gambit.locationSummary?.name || gambit.title, time];
}
